import _ from 'lodash';
export { normalizeMarkdown } from '../base/markdown-wrapper/markdown-wrapper';

export enum MessagingAuthorType {
    SUPPORT = 'SUPPORT',
    PROVIDER = 'PROVIDER',
    PATIENT = 'PATIENT',
}

export enum MessagingOrderEnum {
    ASCENDING = 'ASCENDING',
    DESCENDING = 'DESCENDING',
}

export enum MessagingSortType {
    CREATED = 'CREATED',
    UPDATED = 'UPDATED',
    OPEN = 'OPEN',
    READ = 'READ',
    SUBJECT = 'SUBJECT',
    AUTHOR = 'AUTHOR',
}

export enum MessagingTagType {
    USER = 'USER',
    SYSTEM = 'SYSTEM',
}

export interface IMessagingAuthor {
    id?: string;
    nickname?: string;
    type?: keyof typeof MessagingAuthorType;
}

export interface IMessagingTag {
    id?: string;
    name?: string;
    type?: keyof typeof MessagingTagType;
    groupId?: string;
    created?: string;
}

export interface IMessagingMessage {
    id?: string;
    subject?: string;
    comments?: IMessagingComment[] | number; // Array from API, number for backward compatibility
    /** Precomputed plain-text preview for list rows — prefer this over attaching comment objects. */
    lastCommentPreview?: string;
    /** Full plain-text of the first comment (oldest by date) — used by CSV export without retaining comment graphs. */
    firstCommentBody?: string;
    commentCount?: number;
    documents?: number;
    documentCount?: number;
    open?: boolean;
    read?: boolean;
    author?: IMessagingAuthor;
    lastAuthor?: IMessagingAuthor;
    tags?: IMessagingTag[];
    updated?: string;
    created?: string;
    claimPatientFirst?: string;
    claimPatientLast?: string;
    claimGuarantorFirst?: string;
    claimGuarantorLast?: string;
    claimFromDos?: string;
    claimThroughDos?: string;
    claim?: string;
    /** Linked ENTER claim IDs (from message links). */
    claimIds?: string[];
    from?: string;
    purposeType?: string;
    priority?: string;
    categories?: string[];
    assignees?: string[];
    assigneeIds?: string[];
    dueDate?: string;
}

export interface IMessagingComment {
    id?: string;
    body?: string;
    note?: boolean;
    documents?: string[];
    author?: IMessagingAuthor;
    created?: string;
}

export const authorNickname = (message: IMessagingMessage | IMessagingComment | undefined): string => {
    return getNickname(_.get(message, 'author'));
};

/** UI display nicknames (after getNickname). */
export const ENTER_INC_DISPLAY_NICKNAME = 'Enter, Inc.';
export const ENTER_SUPPORT_DISPLAY_NICKNAME = 'Enter Support';
/** Raw API nickname for ENTER Support / AI (shown as Enter Support in UI). */
export const ENTER_SUPPORT_API_NICKNAME = 'Business Account';

export type MessagingAuthorSource = IMessagingMessage | IMessagingComment | IMessagingAuthor | undefined;

export const getMessagingAuthor = (
    source: MessagingAuthorSource | string,
): IMessagingAuthor | undefined => {
    if (source == null || typeof source === 'string') {
        return undefined;
    }
    if ('author' in source && source.author !== undefined) {
        return source.author;
    }
    if ('nickname' in source || 'type' in source) {
        return source as IMessagingAuthor;
    }
    return undefined;
};

/** Raw API author is ENTER Support (displayed as "Enter Support" via getNickname). */
export const isEnterSupportAuthor = (author: IMessagingAuthor | undefined): boolean => {
    const nickname = _.get(author, 'nickname');
    const type = _.get(author, 'type');
    return nickname === ENTER_SUPPORT_API_NICKNAME && type === MessagingAuthorType.SUPPORT;
};

export const isEnterIncAuthor = (author: IMessagingAuthor | undefined): boolean =>
    _.get(author, 'nickname') === ENTER_INC_DISPLAY_NICKNAME;

export const isEnterDisplayNickname = (nickname: string): boolean =>
    nickname === ENTER_INC_DISPLAY_NICKNAME || nickname === ENTER_SUPPORT_DISPLAY_NICKNAME;

/**
 * True when the author is ENTER (Enter, Inc. or Enter Support / Business Account + SUPPORT).
 * Pass a message, comment, author object, or a display nickname string.
 * Use this everywhere for Resolve, composer, AI badge / AI Message pill, ENTER avatar.
 */
export const isEnterAuthor = (source: MessagingAuthorSource | string): boolean => {
    if (source == null || source === '') {
        return false;
    }
    if (typeof source === 'string') {
        return isEnterDisplayNickname(source);
    }
    const author = getMessagingAuthor(source);
    if (!author) {
        return false;
    }
    return isEnterIncAuthor(author) || isEnterSupportAuthor(author);
};

/** @deprecated Prefer isEnterAuthor */
export const isEnterTaskSender = isEnterAuthor;

export const getNickname = (author: IMessagingAuthor | undefined, fullName = false): string => {
    let nickname = _.get(author, 'nickname');
    const type = _.get(author, 'type');

    if (isEnterSupportAuthor(author)) {
        return ENTER_SUPPORT_DISPLAY_NICKNAME;
    }

    if (!fullName && nickname && nickname !== ENTER_INC_DISPLAY_NICKNAME && type === MessagingAuthorType.SUPPORT) {
        const pair = _.split(nickname, ' ');
        const firstName = _.first(pair);
        const lastInitial = _.head(_.last(pair));
        nickname = `${firstName} ${lastInitial}.`;
    }
    return nickname || '';
};

export const isLastAuthor = (message: IMessagingMessage, author: IMessagingAuthor): boolean => {
    return _.get(message, 'lastAuthor.id') === _.get(author, 'id');
};

const commentsWithValidCreatedDates = (
    comments: IMessagingComment[],
): IMessagingComment[] =>
    comments.filter(comment => {
        if (!comment.created) {
            return false;
        }
        const created = new Date(comment.created);
        return !isNaN(created.getTime());
    });

export const getLastComment = (message: IMessagingMessage): IMessagingComment | undefined => {
    const { comments } = message;
    if (!Array.isArray(comments) || comments.length === 0) {
        return undefined;
    }

    const commentsWithValidCreated = commentsWithValidCreatedDates(comments);

    if (commentsWithValidCreated.length > 0) {
        return _.maxBy(commentsWithValidCreated, comment => new Date(comment.created!).getTime());
    }

    return _.last(comments);
};

/** Oldest comment by `created` — the original message body shown in chat. */
export const getFirstComment = (message: IMessagingMessage): IMessagingComment | undefined => {
    const { comments } = message;
    if (!Array.isArray(comments) || comments.length === 0) {
        return undefined;
    }

    const commentsWithValidCreated = commentsWithValidCreatedDates(comments);

    if (commentsWithValidCreated.length > 0) {
        return _.minBy(commentsWithValidCreated, comment => new Date(comment.created!).getTime());
    }

    return _.first(comments);
};

export const getCommentPreview = (body: string | undefined, maxLength = 120): string => {
    if (!body) {
        return '';
    }

    const plainText = body
        .replace(/<[^>]*>/g, ' ')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/[`*_#>~]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    if (!plainText) {
        return '';
    }

    if (plainText.length <= maxLength) {
        return plainText;
    }

    return `${plainText.slice(0, maxLength).trimEnd()}…`;
};

/** Flat preview string for list rows — avoids retaining comment objects on the table payload. */
export const getLastCommentPreview = (message: IMessagingMessage, maxLength = 120): string | undefined => {
    const preview = getCommentPreview(getLastComment(message)?.body, maxLength);
    return preview || undefined;
};

/** Full plain-text of the first (oldest) comment — for CSV export without keeping comment objects. */
export const getFirstCommentBody = (message: IMessagingMessage): string | undefined => {
    const body = getCommentPreview(getFirstComment(message)?.body, Number.MAX_SAFE_INTEGER);
    return body || undefined;
};

/** Slim table row used by MessageListRedesigned (no author/comment graphs). */
export type IMessagingTableRow = {
    id?: string;
    subject?: string;
    read?: boolean;
    open?: boolean;
    created?: string;
    updated?: string;
    dueDate?: string;
    authorName?: string;
    lastAuthorName?: string;
    purposeType?: string;
    priority?: string;
    documents?: number;
    lastCommentPreview?: string;
    firstCommentBody?: string;
    categories?: string[];
    assignees?: string[];
    assigneeIds?: string[];
    claimIds?: string[];
};

/** Minimal fields needed to build a slim messaging table row. */
export type MessagingTableRowSource = {
    id?: string;
    subject?: string;
    read?: boolean;
    open?: boolean;
    created?: string;
    updated?: string;
    dueDate?: string;
    author?: IMessagingAuthor | any;
    lastAuthor?: IMessagingAuthor | any;
    purposeType?: string;
    priority?: string;
    documentCount?: number;
    lastCommentPreview?: string;
    firstCommentBody?: string;
    claimIds?: string[];
    claim?: string;
    links?: { claims?: Array<{ id?: string }> };
};

export type MessagingTableRowsCache<T extends MessagingTableRowSource = MessagingTableRowSource> = {
    source: T[];
    mapped: IMessagingTableRow[];
};

export type MapMessagingTableRowsOptions<T extends MessagingTableRowSource = MessagingTableRowSource> = {
    getCategoryNames: (message: T) => string[];
    getAssigneeNames: (message: T) => string[];
    getAssigneeIds?: (message: T) => string[];
    getClaimIds?: (message: T) => string[];
    /** Effective read flag for the row (e.g. lastKnown override). Defaults to message.read. */
    getRead?: (message: T) => boolean | undefined;
    /** Previous map result — reuse rows when the source message object is unchanged. */
    previous?: MessagingTableRowsCache<T> | null;
};

/** Split a listing field that may be one id, many ids, or a comma-joined string. */
export const normalizeClaimIds = (ids?: Array<string | undefined>): string[] => {
    if (!ids || ids.length === 0) {
        return [];
    }
    return _.uniq(
        ids
            .flatMap(id => String(id || '').split(/,\s*/))
            .map(id => id.trim())
            .filter(Boolean),
    );
};

/** Linked claim IDs on a listing row (claimIds, links.claims, or legacy claim). */
export const getMessageClaimIds = (
    message:
        | {
              claimIds?: string[];
              claim?: string;
              links?: { claims?: Array<{ id?: string }> };
          }
        | undefined,
): string[] => {
    if (!message) {
        return [];
    }
    if (message.claimIds && message.claimIds.length > 0) {
        return normalizeClaimIds(message.claimIds);
    }
    const fromLinks = normalizeClaimIds(_.map(message.links?.claims, c => c.id));
    if (fromLinks.length > 0) {
        return fromLinks;
    }
    return message.claim ? normalizeClaimIds([message.claim]) : [];
};

/**
 * Map store messages to slim table rows, reusing prior row objects when content is unchanged
 * (by id + content key — survives MobX replacing message object identity on refresh).
 */
export const messagingRowContentKey = (
    message: MessagingTableRowSource,
    read: boolean | undefined,
): string => {
    const claimIds = message.claimIds?.join(',') || message.claim || '';
    const catIds =
        (message as { links?: { messageCategories?: Array<{ id?: string }> } }).links?.messageCategories
            ?.map(c => c.id)
            .join(',') || '';
    const userIds =
        (message as { links?: { users?: Array<{ id?: string }> } }).links?.users?.map(u => u.id).join(',') ||
        '';
    return [
        message.id || '',
        message.updated || '',
        message.created || '',
        message.open === false ? '0' : '1',
        read ? '1' : '0',
        message.subject || '',
        message.dueDate || '',
        String(message.documentCount ?? ''),
        message.lastCommentPreview || '',
        message.firstCommentBody || '',
        claimIds,
        catIds,
        userIds,
    ].join('\u001f');
};

export const mapMessagingTableRows = <T extends MessagingTableRowSource>(
    messages: T[],
    options: MapMessagingTableRowsOptions<T>,
): { rows: IMessagingTableRow[]; cache: MessagingTableRowsCache<T> } => {
    const previousById = new Map<
        string,
        { source: T; mapped: IMessagingTableRow; contentKey: string }
    >();
    if (options.previous) {
        options.previous.source.forEach((source, i) => {
            const id = source.id;
            const mapped = options.previous!.mapped[i];
            if (id && mapped) {
                const read = options.getRead ? options.getRead(source) : source.read;
                previousById.set(id, {
                    source,
                    mapped,
                    contentKey: messagingRowContentKey(source, read),
                });
            }
        });
    }

    const mapped: IMessagingTableRow[] = messages.map(m => {
        const read = options.getRead ? options.getRead(m) : m.read;
        const contentKey = messagingRowContentKey(m, read);
        const prev = m.id ? previousById.get(m.id) : undefined;

        // Same object identity + same read → reuse.
        if (prev && prev.source === m && prev.mapped.read === read) {
            return prev.mapped;
        }
        // MobX often replaces message objects; reuse when content is unchanged.
        if (prev && prev.contentKey === contentKey) {
            return prev.mapped.read === read ? prev.mapped : { ...prev.mapped, read };
        }

        const asMessaging = m as unknown as IMessagingMessage;

        return {
            id: m.id,
            subject: m.subject,
            read,
            open: m.open,
            created: m.created,
            updated: m.updated,
            authorName: getNickname(m.author as any, true) || 'Unknown',
            lastAuthorName: getNickname(m.lastAuthor as any, true) || 'Unknown',
            purposeType: m.purposeType,
            priority: m.priority,
            documents: m.documentCount,
            // Prefer API-precomputed previews — avoid walking comment graphs on every remap.
            lastCommentPreview: m.lastCommentPreview ?? getLastCommentPreview(asMessaging),
            // List UI does not need first comment body; export computes it lazily if missing.
            firstCommentBody: m.firstCommentBody,
            categories: options.getCategoryNames(m),
            assignees: options.getAssigneeNames(m),
            assigneeIds: options.getAssigneeIds ? options.getAssigneeIds(m) : undefined,
            dueDate: m.dueDate,
            claimIds: options.getClaimIds ? options.getClaimIds(m) : getMessageClaimIds(m),
        };
    });

    return { rows: mapped, cache: { source: messages, mapped } };
};

/** Build O(1) id→name maps once per categories/users change (avoids _.find per cell). */
export const buildCategoryNameById = (
    categories: Array<{ id?: string; name?: string }> | undefined,
): Map<string, string> => {
    const map = new Map<string, string>();
    (categories || []).forEach(c => {
        if (c.id && c.name) {
            map.set(c.id, c.name);
        }
    });
    return map;
};

export const buildUserNameById = (
    users: Array<{ userId?: string; personalAccountId?: string; first?: string; last?: string }> | undefined,
): Map<string, string> => {
    const map = new Map<string, string>();
    (users || []).forEach(u => {
        const name = [u.first, u.last].filter(Boolean).join(' ').trim();
        if (!name) {
            return;
        }
        if (u.userId) {
            map.set(u.userId, name);
        }
        if (u.personalAccountId) {
            map.set(u.personalAccountId, name);
        }
    });
    return map;
};

export const sortMessages = (
    messages: IMessagingMessage[],
    sorts: MessagingSortType[] = [],
    orders: MessagingOrderEnum[] = [],
): IMessagingMessage[] => {
    // sort results
    let sortCriteria: string[] = _.map(sorts, (sort: MessagingSortType) => _.toLower(sort));
    if (_.isEmpty(sortCriteria)) {
        sortCriteria.push('updated');
    }
    // NB: extra magic for sorting by author
    sortCriteria = _.map(sortCriteria, sort => (sort === 'author' ? 'author.nickname' : sort));

    // NB: expect both provider/support enums to have 'ASCENDING'
    const orderCriteria = _.map(orders, order => (String(order) === 'ASCENDING' ? 'asc' : 'desc'));
    if (_.isEmpty(orderCriteria)) {
        orderCriteria.push('desc');
    }
    return _.orderBy(messages, sortCriteria, orderCriteria);
};

const MD_AUTOLINK_RE = /<\s*(https?:\/\/|mailto:)[^>]+>/i;

const MD_RE =
    /(```[\s\S]*?```|(^|\n)#{1,6}\s+|(^|\n)\s*>\s+|(^|\n)\s*([-*+])\s+|(^|\n)\s*\d+\.\s+|\[[^\]]+\]\([^)]+\)|`[^`\n]+`|(\*\*|__)[\s\S]+?\1|(\*|_)[^\s].*?\2)/m;

const HTML_DOC_RE = /<\/?(html|head|body)\b/i;
const HTML_TAG_RE = /<\/?[a-z][a-z0-9-]*\b[^>]*>/i;

// Detect whether text looks like Markdown
export const isMarkdownContent = (text: string): boolean => {
    if (!text) return false;

    // Markdown autolinks like <https://example.com> or <mailto:test@example.com>
    if (MD_AUTOLINK_RE.test(text)) return true;

    // Common markdown syntax signals
    return MD_RE.test(text);
};

// Detect whether text should be treated as HTML
export const isHtmlContent = (text: string): boolean => {
    if (!text) return false;

    // Full HTML documents are always HTML
    if (HTML_DOC_RE.test(text)) return true;

    // If it looks like markdown, prefer markdown over HTML
    if (isMarkdownContent(text)) return false;

    // Exclude markdown-style autolinks
    if (MD_AUTOLINK_RE.test(text)) return false;

    // If it contains real-looking HTML tags, treat as HTML
    return HTML_TAG_RE.test(text);
};

// Very lightweight HTML sanitization (safe only for trusted or semi-trusted input)
export const sanitizeHtml = (html: string): string => {
    if (!html) return html;

    return (
        html
            // Remove all <script> blocks
            .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
            // Remove inline event handlers like onclick="", onload="", etc.
            .replace(/\son\w+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, '')
            // If a full document is passed, keep only <body> content
            .replace(/^[\s\S]*<body[^>]*>/i, '')
            .replace(/<\/body>[\s\S]*$/i, '')
            .trim()
    );
};

export const MESSAGE_EXPORT_CSV_HEADERS = [
    'ID',
    'Claim ID',
    'Created By',
    'Created',
    'Updated By',
    'Updated',
    'Subject',
    'Comment',
    'Priority',
    'Purpose',
    'Categories',
    'Assignees',
    'Read',
    'Open',
] as const;

export const messageToCsvRow = (
    message: IMessagingMessage & { categories?: string[]; assignees?: string[] },
): Record<string, string> => {
    const formatDate = (value?: string): string => {
        if (!value) return '';
        const parsed = new Date(value);
        if (isNaN(parsed.getTime())) return String(value);
        return parsed.toLocaleString();
    };

    const priority = message.priority?.toUpperCase();
    const priorityLabel =
        !priority || priority === '-' || priority === 'NONE' ? 'None' : _.startCase(_.toLower(priority));

    return {
        ID: message.id || '',
        'Claim ID': getMessageClaimIds(message).join(', '),
        'Created By': getNickname(message.author, true) || (message as any).authorName || '',
        Created: formatDate(message.created),
        'Updated By': getNickname(message.lastAuthor, true) || (message as any).lastAuthorName || '',
        Updated: formatDate(message.updated),
        Subject: message.subject || '',
        Comment: message.firstCommentBody || getFirstCommentBody(message) || '',
        Priority: priorityLabel,
        Purpose: message.purposeType || '',
        Categories: (message.categories || []).join(', '),
        Assignees: (message.assignees || []).join(', '),
        Read: message.read ? 'Yes' : 'No',
        Open: message.open ? 'Yes' : 'No',
    };
};
